/**
 * Team Inbox — number-registry reader (Integration Plan §4).
 *
 * ONE registry drives every chip, filter, dropdown and (Phase 2) pool:
 * Firestore `settings/wa_numbers`, admin-editable from S6. When the doc
 * is ABSENT (it is not seeded in prod yet — lane rule: no prod writes),
 * the reader falls back to the two hardcoded LIVE entries + the disabled
 * 7121 placeholder. The fallback values are COPIED from
 * src/lib/receivingPhone.ts (copy, don't import-modify — that file stays
 * the legacy screen's source and is untouched per the lane rules).
 *
 * N-number ready: nothing below assumes a count. Adding/renaming a number
 * is a registry edit, zero code (R-A2).
 *
 * Pure helpers are SDK-free so the Node test runner exercises them
 * directly; the Firestore subscription is an impure seam at the bottom
 * (same discipline as lib/paymentMode.ts `listenPaymentSettings`).
 */
import type {
  AllocationConfig,
  NotificationDefaults,
  NumberRegistry,
  WaNumber,
  WorkingHours,
} from './types';

/* ───────────────────── hardcoded boot fallback ───────────────────── */

/**
 * The two LIVE lines (values copied from receivingPhone.ts KNOWN +
 * RECEIVING_NUMBER_ROLES) + the 7121 placeholder (disabled until its
 * coexistence QR scan completes and an admin fills phoneId + enables it).
 * Colours per UI Spec §10 (Sales #b45309 · Client Care #2563eb · Public
 * #15803d).
 */
export const FALLBACK_NUMBERS: WaNumber[] = [
  {
    phoneId: '926096937247472', // +91 99933 17711 (OneHash-liberated, primary)
    line: '+91 99933 17711',
    label: 'Sales',
    colour: '#b45309',
    pool: ['khilendra'], // owner-locked Phase-2 default: Khilendra in BOTH pools
    enabled: true,
  },
  {
    phoneId: '1088402821017863', // +91 91793 03585 (AiSensy-liberated)
    line: '+91 91793 03585',
    label: 'Client Care',
    colour: '#2563eb',
    pool: ['khilendra'], // owner-locked Phase-2 default
    enabled: true,
  },
  {
    phoneId: null, // +91 80154 17121 — fills in after the QR scan (config only)
    line: '+91 80154 17121',
    label: 'Public',
    colour: '#15803d',
    pool: [],
    enabled: false,
  },
];

export const FALLBACK_ALLOCATION: AllocationConfig = {
  mode: 'round_robin',
  escalateAfterMin: 15,
  fallback: ['khilendra', 'rohit'],
  workingHours: null,
};

/** Owner-locked notification defaults (admin = escalations + unassigned). */
export const FALLBACK_NOTIFICATIONS: NotificationDefaults = {
  adminScope: 'escalations_unassigned',
  quietHours: null,
};

/** The registry the UI boots with before (or without) the Firestore doc. */
export function fallbackRegistry(): NumberRegistry {
  return {
    numbers: FALLBACK_NUMBERS.map((n) => ({ ...n, pool: [...n.pool] })),
    allocation: {
      ...FALLBACK_ALLOCATION,
      fallback: [...FALLBACK_ALLOCATION.fallback],
    },
    notifications: { ...FALLBACK_NOTIFICATIONS },
    fromFallback: true,
  };
}

/* ───────────────── Phase-2 sub-normalisers ───────────────── */

const HHMM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** PURE — normalise a workingHours config; null when absent/invalid. */
export function normaliseWorkingHours(raw: unknown): WorkingHours | null {
  const w = (raw && typeof raw === 'object' ? raw : null) as Record<
    string,
    unknown
  > | null;
  if (!w) return null;
  const start = HHMM_RE.test(String(w.start || '')) ? String(w.start) : null;
  const end = HHMM_RE.test(String(w.end || '')) ? String(w.end) : null;
  if (!start || !end || start === end) return null;
  const days = Array.isArray(w.days)
    ? [
        ...new Set(
          (w.days as unknown[]).filter(
            (d): d is number => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6,
          ),
        ),
      ]
    : [1, 2, 3, 4, 5, 6];
  return {
    start,
    end,
    days,
    tzOffsetMinutes:
      typeof w.tzOffsetMinutes === 'number' ? w.tzOffsetMinutes : 330,
  };
}

/** PURE — normalise notification defaults (owner-locked fallback). */
export function normaliseNotifications(raw: unknown): NotificationDefaults {
  const n = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const q = (n.quietHours && typeof n.quietHours === 'object'
    ? n.quietHours
    : null) as Record<string, unknown> | null;
  const start = q && HHMM_RE.test(String(q.start || '')) ? String(q.start) : null;
  const end = q && HHMM_RE.test(String(q.end || '')) ? String(q.end) : null;
  return {
    adminScope: n.adminScope === 'all' ? 'all' : 'escalations_unassigned',
    quietHours: start && end && start !== end ? { start, end } : null,
  };
}

/* ───────────────────── pure normalisation ───────────────────── */

/**
 * PURE — normalise a raw `settings/wa_numbers` doc into the registry the
 * UI consumes. Defensive against partial / hand-edited docs: a malformed
 * entry is dropped, missing allocation fields take the fallback defaults,
 * and an EMPTY/absent numbers array returns the full hardcoded fallback
 * (so a bad admin edit can never blank the inbox chips).
 */
export function normaliseRegistry(raw: unknown): NumberRegistry {
  const doc = (raw && typeof raw === 'object' ? raw : {}) as {
    numbers?: unknown;
    allocation?: unknown;
  };
  const list = Array.isArray(doc.numbers) ? doc.numbers : [];
  const numbers: WaNumber[] = [];
  for (const item of list) {
    const n = (item && typeof item === 'object' ? item : {}) as Record<
      string,
      unknown
    >;
    const line = typeof n.line === 'string' ? n.line.trim() : '';
    const label = typeof n.label === 'string' ? n.label.trim() : '';
    if (!line && !label) continue; // unrecognisable entry — drop
    numbers.push({
      phoneId:
        typeof n.phoneId === 'string' && n.phoneId.trim()
          ? n.phoneId.trim()
          : null,
      line,
      label: label || line,
      colour:
        typeof n.colour === 'string' && n.colour.trim()
          ? n.colour.trim()
          : '#6b7280',
      pool: Array.isArray(n.pool)
        ? n.pool.filter((p): p is string => typeof p === 'string')
        : [],
      enabled: n.enabled === true,
    });
  }
  if (numbers.length === 0) return fallbackRegistry();

  const a = (doc.allocation && typeof doc.allocation === 'object'
    ? doc.allocation
    : {}) as Record<string, unknown>;
  const allocation: AllocationConfig = {
    mode: a.mode === 'manual' ? 'manual' : 'round_robin',
    escalateAfterMin:
      typeof a.escalateAfterMin === 'number' && a.escalateAfterMin > 0
        ? a.escalateAfterMin
        : FALLBACK_ALLOCATION.escalateAfterMin,
    fallback: Array.isArray(a.fallback)
      ? a.fallback.filter((f): f is string => typeof f === 'string')
      : [...FALLBACK_ALLOCATION.fallback],
    workingHours: normaliseWorkingHours(a.workingHours),
  };
  return {
    numbers,
    allocation,
    notifications: normaliseNotifications(
      (doc as { notifications?: unknown }).notifications,
    ),
    fromFallback: false,
  };
}

/**
 * PURE — the numbers every NON-settings surface renders (chips, filter
 * dropdown, composer attribution): enabled + configured (phoneId known)
 * only. A disabled / not-yet-scanned line (7121) is invisible everywhere
 * except Settings (UI Spec §9 "Number not yet live").
 */
export function visibleNumbers(reg: NumberRegistry): WaNumber[] {
  return reg.numbers.filter((n) => n.enabled && !!n.phoneId);
}

/**
 * PURE — resolve a message/conversation's receivingPhoneNumberId to its
 * registry entry. `null` for absent ids (legacy rows pre-8319bb1 — render
 * NO chip, never "undefined") and for ids not in the registry.
 */
export function numberForPhoneId(
  reg: NumberRegistry,
  receivingPhoneNumberId: string | null | undefined,
): WaNumber | null {
  const id = String(receivingPhoneNumberId || '').trim();
  if (!id) return null;
  return reg.numbers.find((n) => n.phoneId === id) || null;
}

/**
 * PURE — options for the per-number filter dropdown (R-A4): "All numbers"
 * first, then each VISIBLE registry number in registry order with its
 * conversation count, then any unregistered id that actually has traffic
 * (a new line stays reachable before it is given a registry entry —
 * mirrors messagesStore.receivingFilterOptions, rebuilt over the registry).
 */
export function numberFilterOptions(
  reg: NumberRegistry,
  rows: Array<{ receivingPhoneNumberId?: string | null }>,
): Array<{ id: string; label: string; line: string; count: number }> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const id = String(r.receivingPhoneNumberId || '').trim();
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const options: Array<{
    id: string;
    label: string;
    line: string;
    count: number;
  }> = [{ id: 'all', label: 'All numbers', line: '', count: rows.length }];
  const seen = new Set<string>();
  for (const n of visibleNumbers(reg)) {
    if (!n.phoneId) continue;
    seen.add(n.phoneId);
    options.push({
      id: n.phoneId,
      label: n.label,
      line: n.line,
      count: counts.get(n.phoneId) || 0,
    });
  }
  for (const [id, count] of counts) {
    if (seen.has(id)) continue;
    options.push({ id, label: id, line: '', count });
  }
  return options;
}

/* ───────────────────── Firestore seam (impure) ───────────────────── */

/**
 * Subscribe to the registry. Emits IMMEDIATELY with the hardcoded
 * fallback, then live-updates from `settings/wa_numbers` whenever the doc
 * exists (admin edits propagate to every open session). A missing doc or
 * a listener error keeps the fallback — the inbox never loses its chips.
 * `settings/` team-read is already allowed by the live rules.
 *
 * Lazy-imports firebase so the pure helpers above stay SDK-free for the
 * Node test runner (same discipline as messagesStore.ts).
 */
export function subscribeNumberRegistry(
  cb: (reg: NumberRegistry) => void,
): () => void {
  cb(fallbackRegistry());
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    try {
      const [{ db }, { doc, onSnapshot }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/firestore'),
      ]);
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, 'settings', 'wa_numbers'),
        (snap) => {
          if (snap.exists()) cb(normaliseRegistry(snap.data()));
          else cb(fallbackRegistry());
        },
        () => {
          /* listener error → fallback already emitted; keep it */
        },
      );
    } catch {
      /* SDK unavailable (harness/test) → fallback already emitted */
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
